import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CalendarClock, X, Download, Trash2, Pencil, Check, Shield } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import PushToggle from "../components/PushToggle";
import PushOptInBanner from "../components/PushOptInBanner";
import ChangePasswordCard from "../components/ChangePasswordCard";

export default function CustomerDashboard() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [profileErr, setProfileErr] = useState("");
  const [profileBusy, setProfileBusy] = useState(false);

  // Non-customer roles landing here (e.g. staff_user navigated via Header
  // 'Account' link) shouldn't trigger customer-only API calls. Redirect.
  useEffect(() => {
    if (!user) return;
    if (user.role === "business" || user.role === "staff_user") nav("/business", { replace: true });
    else if (user.role === "admin") nav("/admin", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
  }, [user]);

  async function saveProfile() {
    setProfileErr("");
    setProfileBusy(true);
    try {
      await api.put("/me", profile);
      if (refresh) await refresh();
      setEditing(false);
    } catch (e) {
      setProfileErr(e.response?.data?.detail || "Errore");
    } finally {
      setProfileBusy(false);
    }
  }

  async function load() {
    const { data } = await api.get("/bookings/mine");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  const now = new Date();
  const upcoming = useMemo(() => items.filter(b => new Date(b.start_at) >= now && b.status !== "cancelled"), [items]);
  const past = useMemo(() => items.filter(b => new Date(b.start_at) < now || b.status === "cancelled"), [items]);
  const list = tab === "upcoming" ? upcoming : past;

  async function cancel(id) {
    if (!window.confirm("Annullare questa prenotazione?")) return;
    await api.post(`/bookings/${id}/cancel`);
    load();
  }

  async function exportData() {
    const { data } = await api.get("/me/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `prenotoo-miei-dati-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
  }

  async function deleteAccount() {
    // Try email-token flow first
    try {
      const { data, status } = await api.post("/me/delete-request");
      if (status === 200 && data?.sent_to) {
        alert(`Ti abbiamo inviato un link di conferma a ${data.sent_to}. Cliccalo entro ${data.expires_in_min} minuti per completare la cancellazione.`);
        return;
      }
    } catch (ex) {
      if (ex.response?.status !== 202) {
        alert(ex.response?.data?.detail || "Errore nell'invio email. Riprova.");
        return;
      }
      // 202 = system email disabled → fall through to direct confirm
    }
    const ok = window.confirm(
      "Cancellare definitivamente il tuo account?\n\n" +
      "Le prenotazioni passate verranno anonimizzate (obblighi fiscali).\n" +
      "Non potrai accedere nuovamente con questa email.\n\n" +
      "Procedere?"
    );
    if (!ok) return;
    try {
      await api.delete("/me");
      await logout();
      nav("/");
    } catch (e) {
      alert("Errore nella cancellazione. Contatta il supporto.");
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 dark:text-zinc-400">Ciao, {user?.name}</div>
          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">Le tue prenotazioni</h1>
        </div>
      </div>

      {/* Soft prompt for push opt-in (auto-hides if subscribed/denied/dismissed) */}
      <PushOptInBanner hint="Ricevi un avviso istantaneo per conferme, promemoria e posti liberi in lista d'attesa. Funziona anche se l'attività non manda email." />

      <div className="grid grid-cols-2 gap-1 p-1 bg-zinc-100 rounded-full max-w-sm mb-6 dark:bg-zinc-800">
        <button onClick={() => setTab("upcoming")} className={`h-10 rounded-full text-sm font-medium ${tab === "upcoming" ? "bg-white shadow" : "text-zinc-600"}`} data-testid="tab-upcoming">
          Prossime ({upcoming.length})
        </button>
        <button onClick={() => setTab("past")} className={`h-10 rounded-full text-sm font-medium ${tab === "past" ? "bg-white shadow" : "text-zinc-600"}`} data-testid="tab-past">
          Storico ({past.length})
        </button>
      </div>

      {list.length === 0 && <div className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="bookings-empty">Nessuna prenotazione.</div>}
      <div className="space-y-3">
        {list.map((b) => (
          <div key={b.id} className="card-soft p-5 flex items-center gap-4" data-testid={`booking-${b.id}`}>
            <div className="h-12 w-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center dark:bg-blue-950/40">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{b.service_name}</div>
              <div className="text-sm text-zinc-500 truncate dark:text-zinc-400">{b.business_name}</div>
              <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">
                {new Date(b.start_at).toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium">€{b.price}</div>
              <div className={`text-xs mt-1 ${b.status === "cancelled" ? "text-red-500" : "text-emerald-600"}`}>{b.status}</div>
              {tab === "upcoming" && b.status === "confirmed" && (
                <button onClick={() => cancel(b.id)} className="mt-2 text-xs text-zinc-500 hover:text-red-600 inline-flex items-center gap-1 dark:text-zinc-400" data-testid={`cancel-${b.id}`}>
                  <X className="h-3 w-3" /> Annulla
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 pt-8 border-t border-zinc-100 dark:border-zinc-800" data-testid="profile-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-medium">I tuoi dati</h2>
            <p className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">Diritto di rettifica (art. 16 GDPR).</p>
          </div>
          {!editing && (
            <button onClick={() => setEditing(true)} className="btn-secondary h-10 text-sm inline-flex items-center" data-testid="profile-edit-btn">
              <Pencil className="h-4 w-4 mr-2" /> Modifica
            </button>
          )}
        </div>
        {editing ? (
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nome</span>
              <input type="text" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                     className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-blue-500 focus:outline-none dark:border-zinc-600"
                     data-testid="profile-name" />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Email</span>
              <input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                     className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-blue-500 focus:outline-none dark:border-zinc-600"
                     data-testid="profile-email" />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Telefono</span>
              <input type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                     className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-blue-500 focus:outline-none dark:border-zinc-600"
                     data-testid="profile-phone" />
            </label>
            {profileErr && <div className="text-sm text-rose-600" data-testid="profile-error">{profileErr}</div>}
            <div className="flex gap-2">
              <button onClick={() => { setEditing(false); setProfile({ name: user.name || "", email: user.email || "", phone: user.phone || "" }); }}
                      className="btn-secondary flex-1" data-testid="profile-cancel">Annulla</button>
              <button onClick={saveProfile} disabled={profileBusy}
                      className="btn-accent flex-1 inline-flex items-center justify-center"
                      data-testid="profile-save"><Check className="h-4 w-4 mr-1" />{profileBusy ? "Salvo…" : "Salva"}</button>
            </div>
          </div>
        ) : (
          <div className="space-y-1 text-sm">
            <div><span className="text-zinc-500 dark:text-zinc-400">Nome:</span> <strong>{user?.name || "—"}</strong></div>
            <div><span className="text-zinc-500 dark:text-zinc-400">Email:</span> <strong>{user?.email || "—"}</strong></div>
            <div><span className="text-zinc-500 dark:text-zinc-400">Telefono:</span> <strong>{user?.phone || "—"}</strong></div>
          </div>
        )}
      </div>

      <div className="mt-12 pt-8 border-t border-zinc-100 dark:border-zinc-800" data-testid="security-section">
        <h2 className="font-display text-xl font-medium">Sicurezza</h2>
        <p className="text-sm text-zinc-500 mt-1 mb-4 dark:text-zinc-400">Mantieni il tuo account protetto.</p>
        <ChangePasswordCard />
      </div>

      <div className="mt-12 pt-8 border-t border-zinc-100 dark:border-zinc-800" data-testid="push-section">
        <h2 className="font-display text-xl font-medium">Notifiche</h2>
        <p className="text-sm text-zinc-500 mt-1 mb-4 dark:text-zinc-400">
          Ricevi promemoria e aggiornamenti sulle tue prenotazioni direttamente sul dispositivo.
        </p>
        <PushToggle />
      </div>

      <div className="mt-12 pt-8 border-t border-zinc-100 dark:border-zinc-800" data-testid="gdpr-section">
        <h2 className="font-display text-xl font-medium">Privacy e dati personali</h2>
        <p className="text-sm text-zinc-500 mt-1 mb-4 dark:text-zinc-400">Esercita i tuoi diritti GDPR (art. 15, 17, 20).</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/me/privacy" className="btn-secondary h-11 text-sm inline-flex items-center" data-testid="gdpr-privacy-center-btn">
            <Shield className="h-4 w-4 mr-2" /> Centro Privacy
          </Link>
          <button onClick={exportData} className="btn-secondary h-11 text-sm" data-testid="gdpr-export-btn">
            <Download className="h-4 w-4 mr-2" /> Esporta i miei dati (JSON)
          </button>
          <button onClick={deleteAccount} className="h-11 px-5 rounded-full text-sm font-medium border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center dark:text-red-300 dark:bg-red-950/40" data-testid="gdpr-delete-btn">
            <Trash2 className="h-4 w-4 mr-2" /> Cancella il mio account
          </button>
        </div>
      </div>
    </div>
  );
}
